// src/pages/AdminLoginPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  Lock,
  Visibility,
  VisibilityOff,
  Login,
  Person
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { 
  login, 
  syncAuthFromLocalStorage,
  selectAuthLoading, 
  selectAuthError, 
  selectIsAuthenticated,
  selectIsAdmin,
  selectIsInitialized,
  clearError 
} from '../store/slices/authSlice';

const AdminLoginPage = () => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  
  const loading = useSelector(selectAuthLoading);
  const error = useSelector(selectAuthError);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isAdmin = useSelector(selectIsAdmin);
  const initialized = useSelector(selectIsInitialized);
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Синхронизация при монтиране
  useEffect(() => {
    if (!initialized) {
      dispatch(syncAuthFromLocalStorage());
    }
  }, [dispatch, initialized]);

  // Redirect ако е вече влязъл като админ
  useEffect(() => {
    if (initialized && isAuthenticated && isAdmin) {
      const from = location.state?.from?.pathname || '/admin';
      navigate(from, { replace: true });
    }
  }, [initialized, isAuthenticated, isAdmin, navigate, location]);

  // Изчистване на грешките при зареждане
  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const validateForm = () => {
    const errors = {};
    
    if (!credentials.username.trim()) {
      errors.username = 'Потребителското име е задължително';
    }
    
    if (!credentials.password) {
      errors.password = 'Паролата е задължителна';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      const result = await dispatch(login(credentials)).unwrap();
      
      // console.log('Login result:', result);
      // console.log('User role:', result.user.role);
      // console.log('User roles:', result.user.roles);
      
      // Проверка за admin роля - покриваме всички възможни варианти
      const userRoles = result.user.roles || [];
      const userRole = result.user.role;
      
      const isUserAdmin = 
        userRole === 'ADMIN' || 
        userRole === 'ROLE_ADMIN' ||
        userRoles.includes('ADMIN') || 
        userRoles.includes('ROLE_ADMIN') ||
        userRoles.some(r => r === 'ADMIN' || r === 'ROLE_ADMIN');
      
      // console.log('Is user admin?', isUserAdmin);
      
      if (isUserAdmin) {
        const from = location.state?.from?.pathname || '/admin';
        // console.log('Navigating to:', from);
        navigate(from, { replace: true });
      } else {
        // console.log('User is not admin, navigating to home');
        // Ако потребителят не е админ, показваме грешка вместо да го пращаме към началната страница
        setFormErrors({ 
          username: 'Нямате администраторски права' 
        });
        dispatch(clearError());
      }
    } catch (err) {
      console.error('Login failed:', err);
    }
  };

  // Показваме loader докато проверяваме сесията
  if (!initialized) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5',
        py: 4
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 2
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Lock sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom>
              Административен Вход
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Читалищна карта - Административен панел
            </Typography>
          </Box>

          {error && error.message && (
            <Alert 
              severity="error" 
              sx={{ mb: 3 }}
              onClose={() => dispatch(clearError())}
            >
              {error.message}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Потребителско име"
              type="text"
              value={credentials.username}
              onChange={(e) => {
                setCredentials({
                  ...credentials,
                  username: e.target.value
                });
                // Изчистваме грешките при промяна
                if (formErrors.username) {
                  setFormErrors({});
                }
              }}
              margin="normal"
              required
              error={!!formErrors.username}
              helperText={formErrors.username}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person />
                  </InputAdornment>
                ),
              }}
              disabled={loading}
            />

            <TextField
              fullWidth
              label="Парола"
              type={showPassword ? 'text' : 'password'}
              value={credentials.password}
              onChange={(e) => {
                setCredentials({
                  ...credentials,
                  password: e.target.value
                });
                // Изчистваме грешките при промяна
                if (formErrors.password) {
                  setFormErrors({});
                }
              }}
              margin="normal"
              required
              error={!!formErrors.password}
              helperText={formErrors.password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={loading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              disabled={loading}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Login />}
              sx={{ mt: 3 }}
            >
              {loading ? 'Вход...' : 'Вход'}
            </Button>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                * Достъп само за упълномощени администратори
              </Typography>
            </Box>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default AdminLoginPage;